import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import LegendTable from "../../components/Common/LegendTable";
import TableHeader from "../../components/Common/TableHeader";
import InfoCard from "../../components/Common/InfoCard";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

import { fetchOccupancyInsights } from "../../store/thunks/analyticsThunks";
import { fetchLandlords } from "../../store/thunks/userThunks";
import { useThunk } from "../../hooks/useThunk";
import { formatDateToISO, formatMonthToDateRange } from "../../utils/dateFormating";
import { svgToCanvas } from "../../utils/svgToCanvas";
import showToast from "../../utils/toastNotifications";
import Logo from "../../assets/logo.png";

import { monthArray } from "../../constants/commonConstants";

const formatDate = (date) => {
  if (!date) return undefined;
  const parsedDate = new Date(date);
  return isNaN(parsedDate.getTime())
    ? undefined
    : parsedDate.toISOString().split("T")[0];
};

const PortfolioPerformanceReport = () => {
  const dispatch = useDispatch();
  const { data, isLoading, error } = useSelector((state) => state.analytics);

  const [doFetchLandlords] = useThunk(fetchLandlords);
  const [landlordList, setLandlordList] = useState([]);

  const allOccupancyData = Array.isArray(data?.propertyData)
    ? data.propertyData
    : [];
  const forecastedData = Array.isArray(data?.forecastedData)
    ? data.forecastedData
    : [];

  const [filters, setFilters] = useState({
    propertyId: "",
    landlordId: "",
    month: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const rentChartRef = useRef(null);
  const rentChartTableRef = useRef(null);
  const forecastChartRef = useRef(null);
  const forecastChartTableRef = useRef(null);


  useEffect(() => {
    const getLandlords = async () => {
      const res = await doFetchLandlords({ page: 1, limit: 100 });
      if (res?.success) {
        const landlords = res.response?.data || [];
        setLandlordList(landlords);
      }
    };
    getLandlords();
  }, []);

  useEffect(() => {
    const landlordName = searchParams.get("landlordName") || "";
    const propertyName = searchParams.get("propertyName") || "";
    const month = searchParams.get("month") || "";

    const landlordId = landlordList.find(
      (landlord) => `${landlord.firstName} ${landlord.lastName}` === landlordName
    )?._id;

    const propertyId = allOccupancyData.find(
      (property) => property.propertyName === propertyName
    )?._id;

    setFilters({
      propertyId: propertyId || "",
      landlordId: landlordId || "",
      month: month || "",
    });
  }, [searchParams]);

  const fetchData = useCallback(() => {
    if (isRequestInProgress) return;
    setIsRequestInProgress(true);

    const query = {
      propertyId: filters.propertyId,
      landlordId: filters.landlordId,
      month: filters.month,
    };
    dispatch(fetchOccupancyInsights(query)).finally(() => {
      setIsRequestInProgress(false);
    });
  }, [filters, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const queryParams = new URLSearchParams();

    const selectedLandlord = landlordList.find(
      (landlord) => landlord._id === filters.landlordId
    );
    const selectedProperty = allOccupancyData.find(
      (property) => property._id === filters.propertyId
    );

    queryParams.set("landlordName", selectedLandlord ? `${selectedLandlord.firstName} ${selectedLandlord.lastName}` : "");
    queryParams.set("propertyName", selectedProperty ? selectedProperty.propertyName : "");
    queryParams.set("month", filters.month || "");

    setSearchParams(queryParams);
  };

  const uniquePropertyOptions = useMemo(
    () => {
    if (!allOccupancyData.length) {
      return [
        {
          label: "🚫 No property",
          value: "none",
          disabled: true,
        },
      ];
    }
  
    return allOccupancyData.map((item) => ({
      label: item.propertyName,
      value: item._id,
    }));
  }, 
    [allOccupancyData]
  );

  const selectedProperty = allOccupancyData.find(
    (property) => property._id === filters.propertyId
  );
  const selectedPropertyName = selectedProperty
    ? selectedProperty.propertyName
    : "All properties";

    const selectedLandlord = landlordList.find((l) => l._id === filters.landlordId);
    const selectedLandlordName = selectedLandlord
      ? `${selectedLandlord.firstName} ${selectedLandlord.lastName}`
      : "All landlords";

  const landlordOptions = useMemo(() => {
    return landlordList.map((landlord) => ({
      label: `${landlord.firstName} ${landlord.lastName}`,
      value: landlord._id,
    }));
  }, [landlordList]);

  const filteredData = useMemo(
    () =>
     {
    return allOccupancyData.filter((item) => {
      const matchProperty =
        !filters.propertyId || item._id === filters.propertyId;
      const matchLandlord =
        !filters.landlordId || item.landlordId === filters.landlordId;
      return matchProperty && matchLandlord;
    });
  },
  [allOccupancyData, filters.propertyId, filters.landlordId]
  );

  const filteredForecastedData = useMemo(
    () =>
       {
    // return filters.propertyId
    //     ? forecastedData.filter(
    //         (item) => item.propertyId === filters.propertyId
    //       )
    //     : forecastedData;
    return forecastedData;
  }, 
    [forecastedData, filters.propertyId]
  );
  /*const filteredForecastedData = useMemo(
    () =>
       {
    return filters.propertyId
        ? forecastedData.filter(
            (item) => item.propertyId === filters.propertyId
          )
        : forecastedData;
  }, 
    [forecastedData, filters.propertyId]
  );*/

  const aggregated = useMemo(() => {
    return filteredData.reduce(
      (acc, curr) => {
        acc.rentCollected += parseFloat(curr.rentCollect || "0");
        acc.rentExpected += parseFloat(curr.rentExpected || "0");
        acc.totalUnits += parseInt(curr.numberOfUnits || "0");
        return acc;
      },
      { rentCollected: 0, rentExpected: 0, totalUnits: 0 }
    );
  }, [filteredData]);

  const rentExpected = aggregated.rentExpected === 0 ? 1 : aggregated.rentExpected;
  const rentCollectedPercentage = rentExpected > 0 ? (aggregated.rentCollected / rentExpected) * 100 : 0;
  const rentExpectedPercentage = rentExpected > 0 ? (rentExpected / rentExpected) * 100 : 0;

  const rentChartSeries = useMemo(
    () => [
      {
        name: "Tenant Payment Status",
        data: [rentCollectedPercentage, rentExpectedPercentage],
      },
    ],
    [rentCollectedPercentage, rentExpectedPercentage]
  );

  const rentChartOptions = useMemo(
    () => ({
    chart: {
      type: "bar",
      height: 400,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        horizontal: false,
        columnWidth: "40%",
      },
    },
    dataLabels: {
      enabled: false,
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        colors: ["#000"],
      },
      formatter: (val) => `${val}%`,
    },
    xaxis: {
      categories: ["Collected amount", "Expected amount"],
      labels: {
        style: {
          fontSize: "12px",
          fontWeight: 700,
          colors: ["#00000099", "#00000099"],
        },
      },
    },
    yaxis: {
      title: {
        text: "",
      },
      min: 0,
      max: rentCollectedPercentage > 100 ? rentCollectedPercentage : 100,
        labels: {
          formatter: (val) => `${val}`,
          style: {
            fontSize: "12px",
            fontWeight: 700,
            colors: ["#00000099"],
          },
        },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val}%`,
      },
    },
    colors: ["#9250E5"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        gradientToColors: ["rgba(146, 80, 229, 0.25)"],
        stops: [0, 100],
        opacityFrom: 1,
        opacityTo: 1,
      },
    },
  }),
    [rentCollectedPercentage, rentExpectedPercentage]
  );

  const legendData = useMemo(
    () => [
      {
        label: "Expected",
        value: aggregated.rentExpected.toFixed(2),
        color: "#A068E8",
        percent: rentExpectedPercentage,
      },
      {
        label: "Collected",
        value: aggregated.rentCollected.toFixed(2),
        color: "#6E90F4",
        percent: rentCollectedPercentage,
      },
    ],
    [aggregated, rentExpectedPercentage, rentCollectedPercentage]
  );

  const maxForecastValue = useMemo(
    () =>
      Math.max(
        ...filteredForecastedData.map((item) => parseFloat(item.value)),
        1
      ),
    [filteredForecastedData]
  );

  const forecastedPercentageData = useMemo(
    () =>
      filteredForecastedData.map((item) => ({
        label: item.label,
        value: (parseFloat(item.value) / maxForecastValue) * 100,
      })),
    [filteredForecastedData, maxForecastValue]
  );

  const forecastedChartSeries = useMemo(
    () => [
      {
        name: "Income",
        data: forecastedPercentageData.map((item) =>
          parseFloat(item.value.toFixed(2))
        ),
      },
    ],
    [forecastedPercentageData]
  );

  const forecastedChartOptions = useMemo(
    () => ({
    chart: {
      type: "area",
      height: 400,
      toolbar: { show: false },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        gradientToColors: ["rgba(146, 80, 229, 0.25)"],
        stops: [0, 100],
        opacityFrom: 1,
        opacityTo: 1,
      },
    },
    xaxis: {
      categories: filteredForecastedData.map((item) => item.label),
      title: {
        text: "Months",
        style: { fontSize: "14px", fontWeight: 600 },
      },
      labels: {
        style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
        formatter: (val) => (/^(Pre|Post)-/.test(val) ? "" : val),
      },
    },
    yaxis: {
      min: 0,
      max: 100,
        labels: {
          formatter: (val) => `${val.toFixed(0)}`,
          style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
        },
      title: {
        text: "Income",
        style: { fontSize: "14px", fontWeight: 600 },
      },
    },
    tooltip: {
      y: { formatter: (val) => `${val}%` },
    },
    colors: ["#9250E5"],
  }),
    [filteredForecastedData]
  );

  /*const forecastedChartSeries = useMemo(
    () => [
      {
        name: "Income",
        data: forecastedPercentageData.map((item) =>
          parseFloat(item.value.toFixed(2))
        ),
      },
    ],
    [forecastedPercentageData]
  );*/

  const createPortfolioInfoCardSVG = (data) => {
    const rentCollected = aggregated.rentCollected !== undefined ? aggregated.rentCollected : 'N/A';
    const rentExpected = aggregated.rentExpected !== undefined ? aggregated.rentExpected : 'N/A';
    const propertyCount = data.propertyCount || 0;
    const totalUnits = data.totalUnits || 0;

    let propertyXPosition = 270;
    let propertyYPosition = 70;
    if (propertyCount > 1000 || totalUnits > 1000) {
      propertyXPosition = 25;
      propertyYPosition = 90;
    }

    const svgContent = `
      <svg width="800" height="200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <!-- Gradient for text color -->
          <linearGradient id="textGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#4E6CF0;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#8442E3;stop-opacity:1" />
          </linearGradient>
          <!-- Shadow filters for text and cards -->
          <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="2" flood-color="rgba(45, 63, 115, 0.15)" />
          </filter>
          <filter id="containerShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" flood-color="rgba(0,0,0,0.06)" />
          </filter>
          <!-- Background pattern for cards -->
          <pattern id="cardBg" patternUnits="userSpaceOnUse" width="1" height="1">
            <rect width="1" height="1" fill="#9FB6FB" opacity="0.1" />
          </pattern>
        </defs>
        
        <!-- Main Container Background -->
        <rect x="5" y="5" width="790" height="120" rx="10" fill="white" filter="url()" />
        
        <!-- Rent Collected Card -->
        <g transform="translate(20, 20)">
          <rect width="160" height="80" rx="5" fill="url()" filter="url()" stroke="#f0f0f0" stroke-width="1" />
          <text x="80" y="28" font-family="Arial, sans-serif" font-size="12" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            Rent Collected (UGX)
        </text>
          <text x="80" y="55" font-family="Arial, sans-serif" font-size="22" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
           ${parseFloat(rentCollected).toFixed(2)}
        </text>
        </g>
  
        <!-- Rent Expected Card -->
        <g transform="translate(200, 20)">
          <rect width="160" height="80" rx="5" fill="url()" filter="url()" stroke="#f0f0f0" stroke-width="1" />
          <text x="80" y="28" font-family="Arial, sans-serif" font-size="12" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            Rent Expected (UGX)
        </text>
          <text x="80" y="55" font-family="Arial, sans-serif" font-size="22" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            ${parseFloat(rentExpected).toFixed(2)}
          </text>
        </g>
          <!-- Properties Card -->
        <g transform="translate(380, 20)">
          <rect width="120" height="80" rx="5" fill="url()" filter="url()" stroke="#f0f0f0" stroke-width="1" />
          <text x="60" y="28" font-family="Arial, sans-serif" font-size="12" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            Properties
          </text>
          <text x="60" y="55" font-family="Arial, sans-serif" font-size="22" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            ${propertyCount}
          </text>
        </g>
          <!-- Units Card -->
        <g transform="translate(530, 20)">
          <rect width="120" height="80" rx="5" fill="url()" filter="url()" stroke="#f0f0f0" stroke-width="1" />
          <text x="60" y="28" font-family="Arial, sans-serif" font-size="12" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            Units
          </text>
          <text x="60" y="55" font-family="Arial, sans-serif" font-size="22" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            ${totalUnits}
          </text>
        </g>
        
      </svg>
    `;
    
    return svgContent;
  };

  const downloadPdf = async () => {
    try {

    let month;
      if(!filters.month || filters.month === '') {
        month = new Date().toLocaleString('default', { month: 'long' });
      } else {
        month = filters.month;
      }
      const {startDate, endDate} = formatMonthToDateRange(month);

      let dateRangeText = `${startDate} to ${endDate}`;
      let propertyName = selectedPropertyName;
      let landlordName = selectedLandlordName || "All landlords";

    if (filteredData.length === 0) {
      showToast("info", "No data available for the selected filters.");
      setIsGeneratingPdf(false);
      return;
    }

      const pdf = new jsPDF('p', 'mm', 'a4');

      let pageHeight = pdf.internal.pageSize.height;
      let pageWidth = pdf.internal.pageSize.width;

      let yPosition = 20;
      let xPosition = 20;
      
      // Contact fields 
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      
      pdf.text('Address:', xPosition, yPosition);
      pdf.text('TENAFIX LIMITED, P.O BOX 191810 KAMPALA UGANDA, CENTRAL, KAMPALA, KAWEMPE DIVISION, MULAGO 1, UPPER MAWANDA', xPosition + 15, yPosition + 0);

      pdf.text('Contact:', xPosition, yPosition + 5);
      pdf.text('+256 773 884476, +256 776 003035', xPosition + 15, yPosition + 5);

      pdf.text('Email:', xPosition, yPosition + 10);
      pdf.text('support@tenafix.co', xPosition + 15, yPosition + 10);

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(xPosition, yPosition + 12, pageWidth - 20, yPosition + 12);

      yPosition += 20;

      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Portfolio Performance Report', 20, yPosition);

      yPosition += 10;

      // Report metadata
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      pdf.text('Reporting Period:', 20, yPosition);

      pdf.setTextColor(0, 0, 0);
    pdf.text(dateRangeText, 50, yPosition);
    yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Landlord:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.text(landlordName, 50, yPosition);
    yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Property:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.text(propertyName, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Generated on:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      const now = new Date();
      const generationTime = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;
      pdf.text(generationTime, 50, yPosition);
      yPosition += 15;

      // Applied filters section
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Applied Filters', 20, yPosition);
      yPosition += 8;

      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont("helvetica", "normal");
      pdf.text(`• Landlord Filter: ${landlordName}`, 25, yPosition);
      yPosition += 5;
        pdf.text(`• Property Filter: ${propertyName}`, 25, yPosition);
        yPosition += 5;
      pdf.text(`• Date Range: ${dateRangeText}`, 25, yPosition);
      yPosition += 10;

      // Create SVG InfoCard with gradients
      const portfolioInfoCardContent = createPortfolioInfoCardSVG({
        propertyCount: uniquePropertyOptions.length,
        totalUnits: aggregated.totalUnits,
      });
      
      const portfolioInfoCardCanvas = await svgToCanvas(portfolioInfoCardContent, 800, 200);
      const portfolioInfoCardImgData = portfolioInfoCardCanvas.toDataURL('image/png');
      const portfolioInfoCardWidth = 170; // Full width within margins
      const portfolioInfoCardHeight = (200 * portfolioInfoCardWidth) / 800; // Proportional height

      pdf.addImage(portfolioInfoCardImgData, 'PNG', 20, yPosition, portfolioInfoCardWidth, portfolioInfoCardHeight);
      yPosition += portfolioInfoCardHeight;

      // Rent Collected vs Expected chart
      pdf.setFontSize(14);
      pdf.setTextColor(71, 114, 241);
      pdf.setFont("helvetica", "bold");
      pdf.text('Rent Collected vs. Expected', 20, yPosition);
      yPosition += 10;

      let rentChartHeight = 0;
      if (rentChartRef.current) {
        const canvas = await html2canvas(rentChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 80;
        rentChartHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, rentChartHeight);
      }

      if (rentChartTableRef.current){
        const legendCanvas = await html2canvas(rentChartTableRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const legendImgData = legendCanvas.toDataURL('image/png');
        const legendWidth = 80;
        const legendHeight = (legendCanvas.height * legendWidth) / legendCanvas.width;
        
        pdf.addImage(legendImgData, 'PNG', 100, yPosition + 0, legendWidth, legendHeight);
      }

      yPosition += rentChartHeight + 10;

      pdf.addPage();
      yPosition = 25;

      //Forecast Chart Section
      pdf.setFontSize(14);
      pdf.setTextColor(71, 114, 241);
      pdf.setFont("helvetica", "bold");
      pdf.text('Forecasted Income', 20, yPosition);
      yPosition += 10;

      if (forecastChartRef.current) {
        const canvas = await html2canvas(forecastChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 80;
        const forecastChartHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, forecastChartHeight);
      }

      if (forecastChartTableRef.current){
        const legendCanvas = await html2canvas(forecastChartTableRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const legendImgData = legendCanvas.toDataURL('image/png');
        const legendWidth = 80;
        const legendHeight = (legendCanvas.height * legendWidth) / legendCanvas.width;
        
        pdf.addImage(legendImgData, 'PNG', 100, yPosition, legendWidth, legendHeight);
      }

      // Add header and footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);

        const footerY = pageHeight - 15;

        // header section
        pdf.setFontSize(10);
        pdf.setTextColor(71, 114, 241); // Blue color for brand
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', xPosition, 10);
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('Real Estate Management Software', xPosition, 14);
        
        pdf.addImage(Logo, 'PNG', pageWidth - 40, -5, 30, 30);

        // Footer section
        pdf.setFontSize(8);
        pdf.setTextColor(71, 114, 241);
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', 20, footerY + 3);
        
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('|', 42, footerY + 3);
        pdf.text('www.tenafix.co', 44, footerY + 3);
        pdf.text('|', 64, footerY + 3);
        pdf.text('support@tenafix.co', 66, footerY + 3);
        
        // System generated note
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text('This is a system-generated report.', 20, footerY + 6);
        pdf.text('For assistance contact support@tenafix.co', 20, footerY + 9);
        
        // Page number (right aligned)
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${i} of ${pageCount}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pdf.internal.pageSize.width - pageTextWidth - 20, footerY + 8);
      }

      // Save PDF
      const localNow = new Date().toLocaleString().split(', ');
      const currentDate = `${localNow[0]}/${localNow[1].split(' ')[0]}`.replace(/[/:]/g, '-');
      pdf.save(`portfolio-performance-report-${currentDate}.pdf`);

    } catch (error) {
      showToast("error", "Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }

  }

  const handleDownload = () => {
    setIsGeneratingPdf(true);
    setTimeout(() => downloadPdf(), 0);
  };

  const propertiesCount = filteredData.length;


  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-[30px]">
      <TableHeader
        searchValue={filters.propertyId}
        onSearchSubmit={handleSearch}
        filters={[
          {
            name: "Landlord",
            value: filters.landlordOptions,
            onChange: (val) => {
          //  const selectedLandlord = landlordList.find((ll) => ll._id === val);
              setFilters((prev) => ({ ...prev, landlordId: val }));
            },
            options: [
              { label: "All", value: "" },
              ...landlordOptions,
            ],
          },
          {
            name: "Property",
            value: filters.selectedPropertyName,
            onChange: (val) =>
              setFilters((prev) => ({ ...prev, propertyId: val })),
            options: [
              ...(uniquePropertyOptions.length > 0 &&
              !uniquePropertyOptions[0].disabled
                ? [{ label: "All properties", value: "" }]
                : []),
              ...uniquePropertyOptions,
            ],
          },
          {
            name: "Filter by Month",
            value: filters.month,
            onChange: (val) =>
              setFilters((prev) => ({ ...prev, month: val })),
            options: monthArray.map((month) => ({
              label: month,
              value: month,
            })),
          }
        ]}
        buttons={[
          {
            label: isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "SEARCH"
            ),
            onClick: handleSearch,
            disabled: isLoading,
            variant: "secondary",
            className:
              "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[100px] uppercase font-medium",
          },
          {
            label: "DOWNLOAD REPORT",
            onClick: handleDownload,
            loading: isGeneratingPdf,
            disabled: isGeneratingPdf || isLoading,
            className:
              "bg-[#C084FC] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[170px] uppercase font-bold",
          },
        ]}
        containerClass="bg-white  flex-wrap md:grid md:grid-cols-2 lg:grid lg:grid-cols-2 md:gap-y-[30px] [@media(min-width:1120px)]:grid [@media(min-width:1120px)]:grid-cols-3 [@media(min-width:1516px)]:grid [@media(min-width:1516px)]:grid-cols-4 justify-between  p-3 sm:p-4 md:p-6 lg:p-[30px] "
        filterDropdownClass="w-full md:w-full lg:w-full [@media(min-width:1516px)]:w-[247px] [@media(min-width:1516px)]:w-full"
        searchInputClass="text-gray-700 h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] md:w-full [@media(min-width:1516px)]:w-[247px] [@media(min-width:1516px)]:w-full"
        datePickerClass="gap-2"
        datePickerInputClass="w-full sm:w-full md:w-full lg:w-full [@media(min-width:1516px)]:w-full [@media(min-width:1708px)]:w-[247px] border"
        buttonWrapperClass="justify-center sm:justify-start  md:gap-[10px] lg:gap-[10px] [@media(min-width:1516px)]:gap-[10px] [@media(min-width:1300px)]:ml-[380px] [@media(min-width:1120px)]:ml-[280px]  [@media(min-width:1516px)]:ml-[0px]  [@media(min-width:1516px)]:justify-end"
        buttonClass="uppercase tracking-wide flex justify-center"
      />
      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] $ grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <InfoCard
              label="Rent Collected (UGX)"
              value={aggregated.rentCollected.toFixed(2)}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[16px] sm:text-[20px] md:text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Rent Expected (UGX)"
              value={aggregated.rentExpected.toFixed(2)}
              className=" max-h-[120px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[16px] sm:text-[20px] md:text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Properties"
               // value={filters.propertyId ? 1 : uniquePropertyOptions.length}
              value={filters.propertyId ? 1 : propertiesCount}
              className=" max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[24px] sm:text-[30px] md:text-[40px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Units"
              value={aggregated.totalUnits}
              className=" max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[24px] sm:text-[30px] md:text-[40px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
        </div>
      </div>

      {/* Rent Collected vs Expected */}
      <div id="rent-chart" className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F] grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 lg:gap-[60px] mt-[30px]">
        <div>
          <h2 className="text-[22px] font-semibold text-[#4772F1] leading-[100%] font-inter mb-4">
            Rent Collected vs. Expected
          </h2>
          <div ref={rentChartRef} >
          <ReactApexChart
            options={rentChartOptions}
            series={rentChartSeries}
            type="bar"
            height={400}
          />
          </div>
        </div>

        <div  ref={rentChartTableRef} className="grid items-center ">
          <LegendTable
            data={legendData}
            showDot={true}
            className="mt-4"
            classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
            classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
          />
        </div>
      </div>

      {/* Forecasted Income */}
      <div id="forecasted-chart" className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        <h2 className="text-[22px] font-semibold text-[#4772F1] leading-[100%] font-inter mb-4">
          Forecasted Income
        </h2>
        <div className=" grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 lg:gap-[60px]">
          <div  ref={forecastChartRef}  >
          <ReactApexChart
            options={forecastedChartOptions}
            series={forecastedChartSeries}
            type="area"
            height={400}
          />
          </div>
          <div ref={forecastChartTableRef}   >
          <LegendTable
            data={forecastedPercentageData
              .filter((item) => !/^(Pre|Post)-/.test(item.label))
              .map((item) => ({
                label: item.label,
                value: parseFloat((item.value * maxForecastValue) / 100).toFixed(2),
                percent: parseFloat(item.value.toFixed(2)),
              }))}
            showDot={false}
            className="mt-4"
            classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
            classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
          />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPerformanceReport;
